import React from "react";
// ANT DESIGN COMPONENTS
import { Alert, Table } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CUSTOM HOOKS
import { DELETED_BANKS_PAGE_SIZE } from "../../../customHooks/GET_requests/useDeletedBanks";
// UTILS
import { DeletedBankRow, formatDeletedAt } from "../../../utils/deletedBanks";
import { describeError } from "../../setupWizard/utils/describeError";

interface DeletedBanksTableProps {
  rows: DeletedBankRow[];
  /** The server's total across all pages. */
  total: number;
  page: number;
  loading: boolean;
  /** The RTK Query error of the last request, shown above the table. */
  error?: unknown;
  onPageChange: (page: number) => void;
  /**
   * The cell of the trailing action column. Omitted when the user may list
   * deleted banks but not restore them, which drops the column entirely.
   */
  renderAction?: (row: DeletedBankRow) => React.ReactNode;
}

/**
 * The deleted-banks table both restore surfaces share: the wizard's Banks step
 * and the Edge Management Banks page.
 *
 * It only presents. What a row's action does differs between the two (a
 * confirmation in place in the wizard, a modal on the Banks page), so the
 * action cell is handed in.
 */
function DeletedBanksTable({
  rows,
  total,
  page,
  loading,
  error = undefined,
  onPageChange,
  renderAction = undefined,
}: DeletedBanksTableProps) {
  const [t] = useTranslation("global");

  const columns = [
    { title: t("general.name"), dataIndex: "name", key: "name" },
    { title: t("general.type"), dataIndex: "type", key: "type" },
    {
      title: t("general.logicalPort"),
      dataIndex: "logicalPort",
      key: "logicalPort",
      render: (port: number | null) => (port === null ? "--" : port),
    },
    {
      title: t("edgeManagement.banks.deleted.deletedAt"),
      dataIndex: "deletedAt",
      key: "deletedAt",
      render: (value: string) => formatDeletedAt(value),
    },
    ...(renderAction
      ? [
          {
            title: "",
            key: "actions",
            render: (_: unknown, row: DeletedBankRow) => renderAction(row),
          },
        ]
      : []),
  ];

  return (
    <div>
      <p className="mb-2">{t("edgeManagement.banks.deleted.description")}</p>
      {error !== undefined && error !== null && (
        <Alert type="error" showIcon message={describeError(error)} style={{ marginBottom: 12 }} />
      )}
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loading}
        locale={{ emptyText: t("edgeManagement.banks.deleted.empty") }}
        pagination={{
          current: page,
          pageSize: DELETED_BANKS_PAGE_SIZE,
          total,
          hideOnSinglePage: true,
          showSizeChanger: false,
          onChange: onPageChange,
        }}
      />
    </div>
  );
}

export default DeletedBanksTable;
